import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { PHONE_DISPLAY, TEL_LINK, WA_LINK, scrollToId } from "@/lib/site";
import { LogoMark, WhatsAppIcon } from "./icons";

const LINKS = [
  { label: "Loans", id: "#loans" },
  { label: "EMI Calculator", id: "#calculator" },
  { label: "Why Us", id: "#why" },
  { label: "Process", id: "#process" },
  { label: "FAQ", id: "#faq" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  const go = (e, id) => {
    setOpen(false);
    scrollToId(e, id);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="bg-ink text-paper/80">
        <a
          data-testid="topbar-phone-link"
          href={TEL_LINK}
          className="block text-center font-mono text-[11px] sm:text-xs uppercase tracking-[0.18em] py-2 hover:text-gold transition-colors"
        >
          Loan chahiye? Direct call kijiye — {PHONE_DISPLAY}
        </a>
      </div>

      <nav className="bg-paper/85 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a
            data-testid="navbar-logo-link"
            href="#home"
            onClick={(e) => go(e, "#home")}
            className="flex items-center gap-3"
          >
            <LogoMark className="w-9 h-9" />
            <span className="leading-none">
              <span className="block font-display font-semibold text-lg tracking-tight">
                Satyam <span className="text-gold italic">Financial</span>
              </span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mt-0.5">
                Loan Services
              </span>
            </span>
          </a>

          <div className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <a
                key={l.id}
                data-testid={`nav-link-${l.id.slice(1)}`}
                href={l.id}
                onClick={(e) => go(e, l.id)}
                className="text-sm font-medium text-slate-600 hover:text-ink transition-colors"
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              data-testid="navbar-whatsapp-link"
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:grid place-items-center w-10 h-10 rounded-full border border-black/10 text-brand hover:bg-brand hover:text-white transition-colors"
              aria-label="WhatsApp par baat karein"
            >
              <WhatsAppIcon className="w-[18px] h-[18px]" />
            </a>
            <a
              data-testid="navbar-phone-button"
              href={TEL_LINK}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-ink text-white pl-3 pr-5 py-2.5 text-sm font-semibold hover:bg-brand transition-colors"
            >
              <Phone className="w-4 h-4" />
              {PHONE_DISPLAY}
            </a>
            <button
              data-testid="navbar-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden grid place-items-center w-10 h-10 rounded-full border border-black/10"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="lg:hidden overflow-hidden border-t border-black/5"
              data-testid="navbar-mobile-panel"
            >
              <div className="px-6 py-4 flex flex-col gap-1">
                {LINKS.map((l) => (
                  <a
                    key={l.id}
                    href={l.id}
                    onClick={(e) => go(e, l.id)}
                    className="py-3 text-base font-medium text-slate-700 border-b border-black/5 last:border-0"
                  >
                    {l.label}
                  </a>
                ))}
                <div className="flex gap-3 pt-3 pb-2">
                  <a
                    data-testid="navbar-mobile-phone-button"
                    href={TEL_LINK}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white py-3 text-sm font-semibold"
                  >
                    <Phone className="w-4 h-4" /> Call Karein
                  </a>
                  <a
                    data-testid="navbar-mobile-whatsapp-link"
                    href={WA_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-wa text-white py-3 text-sm font-semibold"
                  >
                    <WhatsAppIcon className="w-4 h-4" /> WhatsApp
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};
